import { useEffect, useState } from 'react';
import { api_with_method } from '../../../../../redux/api_funcs';
import { get_api_url } from '../../../../../utils/urls';

export interface PlanItem {
  session_per_week: number | null;
  months: number | null;
  is_best_value: boolean;
  type: string; // '1on1' | 'group'
}

export interface GroupClassItem {
  uuid: string;
  name: string;
}

export interface Notes {
  coach: string;
  ai: string;
  selected: 'teacher' | 'ai';
}

export interface Lifestyle {
  current: string[];
  goals: string[];
}

export interface PersonalisedPlanPayload {
  plans: PlanItem[];
  recommended_group_classes: GroupClassItem[];
  notes: Notes;
  lifestyle: Lifestyle;
  send_to_student?: boolean;
}

export interface PersonalisedPlanStatus {
  sent_to_student: boolean;
  sent_to_student_at: string | null;
  plans?: any[];
  recommended_classes?: string[];
}

export function useFetchPersonalisedPlanStatus(studentUuid: string) {
  const [planStatus, setPlanStatus] = useState<PersonalisedPlanStatus | null>(null);
  const [isFetchingStatus, setIsFetchingStatus] = useState(true);
  const [fetchStatusError, setFetchStatusError] = useState<string | null>(null);

  useEffect(() => {
    if (!studentUuid) return;

    const fetchStatus = async () => {
      const url = get_api_url(`/v2/cms_user/personalised_plan/${studentUuid}`, null, true);
      setIsFetchingStatus(true);
      setFetchStatusError(null);

      try {
        const res = await api_with_method('get', url, null, true);
        setPlanStatus(res?.data?.data ?? null);
      } catch (err: any) {
        const reason = err?.response?.data?.reason;
        if (reason === 'record not found') {
          // No plan created yet — allow the form to render as a blank slate
          setPlanStatus(null);
        } else {
          console.error('Error fetching personalised plan status', err);
          setFetchStatusError('Failed to fetch personalised plan status');
        }
      } finally {
        setIsFetchingStatus(false);
      }
    };

    fetchStatus();
  }, [studentUuid]);

  return { planStatus, isFetchingStatus, fetchStatusError };
}

export function useSavePersonalisedPlan(studentUuid: string) {
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [shortUrl, setShortUrl] = useState<string | null>(null);

  const savePersonalisedPlan = async (payload: PersonalisedPlanPayload) => {
    const url = get_api_url(`/v2/cms_user/personalised_plan/${studentUuid}`, null, true);
    setIsSaving(true);
    setSaveError(null);
    setShortUrl(null);

    try {
      const res = await api_with_method('post', url, payload, true);

      setShortUrl(res?.data?.data?.short_url ?? null);
    } catch (err) {
      console.error('Error saving personalised plan', err);
      setSaveError('Failed to save personalised plan');
      throw err;
    } finally {
      setIsSaving(false);
    }
  };

  return { savePersonalisedPlan, shortUrl, isSaving, saveError };
}
